import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { virtualCompetitionAPI } from '../api/endpoints';
import {
  FaPlus, FaPlay, FaTimes, FaCopy, FaTrophy, FaUsers,
  FaCoins, FaSync, FaCheckCircle, FaHourglassHalf, FaShareAlt,
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './CreatorVirtualCompetition.css';

const REWARD_MODES = [
  { key: 'equal',    label: 'Equal split' },
  { key: 'weighted', label: 'Weighted (top-heavy)' },
  { key: 'custom',   label: 'Custom %' },
];

const CreatorVirtualCompetition = () => {
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [runningId, setRunningId] = useState(null);

  const [form, setForm] = useState({
    name: '',
    description: '',
    entry_fee: 0,
    prize_pool: 0,
    total_games: 10,
    total_winners: 1,
    reward_mode: 'equal',
    reward_distribution: [],
  });

  const fetchMine = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const res = await virtualCompetitionAPI.listMine();
      setGames(res.data?.data || []);
    } catch (err) {
      if (showLoader) toast.error('Failed to load your competitions');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMine(true); }, [fetchMine]);

  // Auto-refresh while any competition is live
  useEffect(() => {
    const hasLive = games.some((g) => g.status === 'live');
    if (!hasLive) return;
    const t = setInterval(() => fetchMine(false), 20000);
    return () => clearInterval(t);
  }, [games, fetchMine]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        ...form,
        entry_fee: Number(form.entry_fee) || 0,
        prize_pool: Number(form.prize_pool) || 0,
        total_games: Number(form.total_games) || 10,
        total_winners: Number(form.total_winners) || 1,
        reward_distribution: form.reward_mode === 'custom'
          ? form.reward_distribution.map((n) => Number(n) || 0)
          : null,
      };
      const res = await virtualCompetitionAPI.create(payload);
      if (res.data?.success) {
        toast.success('Competition created!');
        setShowCreate(false);
        await fetchMine(false);
      } else {
        toast.error(res.data?.message || 'Failed to create');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const handleRun = async (game) => {
    if (!window.confirm(`Start "${game.name}"? The first 5 games will drop immediately.`)) return;
    setRunningId(game.id);
    try {
      const res = await virtualCompetitionAPI.run(game.id);
      if (res.data?.success) {
        toast.success('Competition live — first batch of games has dropped!');
        await fetchMine(false);
      } else {
        toast.error(res.data?.message || 'Failed to start');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start');
    } finally {
      setRunningId(null);
    }
  };

  const copyLink = (game) => {
    navigator.clipboard.writeText(game.share_url);
    toast.success('Link copied!');
  };

  const updateCustomDistribution = (index, value) => {
    const next = [...form.reward_distribution];
    next[index] = value;
    setForm({ ...form, reward_distribution: next });
  };

  // When total_winners or reward_mode changes, reset the distribution array
  useEffect(() => {
    if (form.reward_mode === 'custom') {
      const arr = Array.from({ length: Math.max(1, Number(form.total_winners)) }, () =>
        Math.floor(100 / Math.max(1, Number(form.total_winners)))
      );
      setForm((prev) => ({ ...prev, reward_distribution: arr }));
    }
   
  }, [form.reward_mode, form.total_winners]);

  return (
    <div className="cvc-page">
      <div className="cvc-header">
        <h1>🏆 Virtual Competitions</h1>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <FaPlus /> New Competition
        </button>
      </div>

      {showCreate && (
        <form className="cvc-create-form" onSubmit={handleCreate}>
          <h3>Create a Virtual Competition</h3>
          <p className="form-hint">
            Players predict 5 matches every 7 minutes. Points accumulate over {form.total_games} games.
          </p>

          <div className="form-group">
            <label>Competition Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Weekend Premier Prediction"
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Entry Fee ($XPUNT)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={form.entry_fee}
                onChange={(e) => setForm({ ...form, entry_fee: e.target.value })}
              />
              <small className="hint">
                {Number(form.entry_fee) > 0
                  ? 'Prize pool = sum of entry fees'
                  : 'You fund the prize pool yourself'}
              </small>
            </div>
            {Number(form.entry_fee) === 0 && (
              <div className="form-group">
                <label>Prize Pool ($XPUNT) *</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form.prize_pool}
                  onChange={(e) => setForm({ ...form, prize_pool: e.target.value })}
                  required
                />
                <small className="hint">Deducted from your wallet</small>
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Total Games *</label>
              <input
                type="number"
                min="5"
                max="100"
                value={form.total_games}
                onChange={(e) => setForm({ ...form, total_games: e.target.value })}
                required
              />
              <small className="hint">
                {Math.ceil(form.total_games / 5)} rounds × 5 games = ~
                {Math.ceil(form.total_games / 5) * 7} minutes
              </small>
            </div>
            <div className="form-group">
              <label>Number of Winners *</label>
              <input
                type="number"
                min="1"
                max={Math.max(1, Number(form.total_games))}
                value={form.total_winners}
                onChange={(e) => setForm({ ...form, total_winners: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Reward Distribution</label>
            <div className="reward-mode-chips">
              {REWARD_MODES.map((mode) => (
                <button
                  key={mode.key}
                  type="button"
                  className={`reward-chip ${form.reward_mode === mode.key ? 'active' : ''}`}
                  onClick={() => setForm({ ...form, reward_mode: mode.key })}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          </div>

          {form.reward_mode === 'custom' && (
            <div className="custom-dist">
              <label>Custom distribution (% per rank)</label>
              <div className="custom-dist-rows">
                {form.reward_distribution.map((pct, i) => (
                  <div key={i} className="custom-dist-row">
                    <span>Rank #{i + 1}</span>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={pct}
                      onChange={(e) => updateCustomDistribution(i, e.target.value)}
                    />
                    <span>%</span>
                  </div>
                ))}
              </div>
              <small className="hint">
                Total: {form.reward_distribution.reduce((s, n) => s + Number(n || 0), 0)}% (must equal 100%)
              </small>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={creating}>
              {creating ? <LoadingSpinner size="small" /> : 'Create Competition'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="cvc-loading"><LoadingSpinner size="large" /></div>
      ) : games.length === 0 ? (
        <div className="cvc-empty">
          <FaTrophy className="empty-icon" />
          <p>No virtual competitions yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="cvc-list">
          {games.map((g) => (
            <div key={g.id} className={`cvc-card status-${g.status}`}>
              <div className="cvc-card-top">
                <span className={`cvc-status ${g.status}`}>{g.status.toUpperCase()}</span>
                <span className="cvc-players">
                  <FaUsers /> {g.player_count} player{g.player_count !== 1 ? 's' : ''}
                </span>
              </div>

              <h3 className="cvc-name">{g.name}</h3>
              {g.description && <p className="cvc-desc">{g.description}</p>}

              <div className="cvc-meta">
                <span>
                  <FaCoins /> {Number(g.prize_pool).toFixed(2)} pool
                </span>
                <span>
                  Entry: {Number(g.entry_fee) > 0
                    ? `${Number(g.entry_fee).toFixed(2)} $XPUNT`
                    : 'Free'}
                </span>
                <span>
                  <FaTrophy /> {g.total_winners} winner{g.total_winners !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Progress */}
              <div className="cvc-progress">
                <div className="cvc-progress-bar">
                  <div
                    className="cvc-progress-fill"
                    style={{
                      width: `${Math.min(100, (g.games_played / g.total_games) * 100)}%`,
                    }}
                  />
                </div>
                <span className="cvc-progress-label">
                  {g.games_played} / {g.total_games} games
                  {g.status === 'live' && ` · Round ${g.current_round}`}
                </span>
              </div>

              {g.status === 'open' && (
                <div className="cvc-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => copyLink(g)} type="button">
                    <FaCopy /> Copy Link
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleRun(g)}
                    disabled={runningId === g.id}
                    type="button"
                  >
                    {runningId === g.id
                      ? <LoadingSpinner size="small" />
                      : <><FaPlay /> Start</>}
                  </button>
                </div>
              )}

              {g.status === 'live' && (
                <div className="cvc-live-note">
                  <FaSync className="spin" /> Dropping 5 games every 7 minutes — Round {g.current_round} in progress
                </div>
              )}

              {g.status === 'completed' && (
                <div className="cvc-completed-note">
                  <FaCheckCircle /> Competition completed — winners notified
                </div>
              )}

              {g.status === 'open' && g.player_count === 0 && (
                <div className="cvc-hint">
                  Share the link — the competition stays open until you click Start.
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreatorVirtualCompetition;